/* eslint-disable jsx-a11y/label-has-associated-control */
import * as React from 'react';
import styled from 'styled-components';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { PDFDocument } from 'pdf-lib';
import { useEffect, useState } from 'react';
import { fillableFormschema, labels, updateFillableFormschema } from '../../../forms/validation/formSetupValidation';
import {
  ButtonWithLoader,
  CheckboxWithLabel,
  InputBaseContainer,
  InputFileContainer,
  InputSelectWithLabel,
  InputTextWithLabel,
  ModalDefaultDraggable,
  TextAreaWithLabel,
} from '../../../molecules';
import { FormFillableSetupType } from '../../../api/formSetup';
import { getBase64 } from '../../../utils/isDevMode';
import isYupFieldRequired from '../../../utils/isYupFieldRequired';
import { FormServerError } from '../../../types';
import { getInspectionTypes } from '../../../api/inspectionTypes';
import {
  useSaveInspectionFormDataMutation,
  useUpdateInspectionFormMutation,
} from '../../../redux/rtkQuery/apiFormbuilderSlice';
import { getJurisdictions } from '../../../api/jurisdiction';
import { GET_ALL_QUERY_PARAMS } from '../../../constants';

// The jurisdictions endpoint can return several records that share the same
// name, which shows up as duplicate dropdown options. Dedupe by name for
// display (the full list is kept for edit pre-fill).
const dedupeByName = (arr: any[]) => {
  const seen = new Set();
  return (arr || []).filter(item => {
    if (!item || seen.has(item.name)) return false;
    seen.add(item.name);
    return true;
  });
};

// "<type> - <jurisdiction>". An InspectionType belongs to exactly one
// jurisdiction and the same name is repeated once per jurisdiction, so the bare
// name renders as a wall of identical chips ("T001 Threshold Concrete" fourteen
// times) with no way to tell which jurisdiction each one covers.
const inspectionTypeLabel = (item: any) => [item?.name, item?.jurisdictionName].filter(Boolean).join(' - ');

const AddNewForm = ({ initialValues, setShowAdd, selectedFontSize }) => {
  const { register, handleSubmit, errors, control, setValue, getValues, watch } = useForm<FormFillableSetupType>({
    resolver: yupResolver(initialValues ? updateFillableFormschema : fillableFormschema),
    defaultValues: initialValues || ({} as FormFillableSetupType),
  });
  const inspectionType = watch('inspectionType');
  const name = watch('name');
  const description = watch('description');
  const jurisdiction = watch('jurisdiction');
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [IsUpdatePlaceholder, setIsUpdatePlaceholder] = React.useState(false);
  const [inspectionsData, setInspectionsData] = React.useState([]);
  const [localUrl, setLocalUrl] = React.useState('');
  const [fileName, setFileName] = React.useState('');
  const [pageWarning, setPageWarning] = useState<string>('');
  const [dataToSave, setDataToSave] = useState();
  const [jurisdictionData, setJurisdictionData] = useState([]);
  const [byJurisdiction, setByJurisdiction] = useState<boolean>(false);
  const jurisdictionOptions = React.useMemo(
    () => dedupeByName(jurisdictionData),
    [jurisdictionData],
  );
  // Deliberately NOT deduped by name: the copies differ only by jurisdiction,
  // so collapsing them left a single arbitrary option and made it impossible to
  // attach the form to the other thirteen jurisdictions. The label carries the
  // jurisdiction now, so they read as distinct.
  const inspectionOptions = React.useMemo(
    () => [...inspectionsData].sort((a, b) => inspectionTypeLabel(a).localeCompare(inspectionTypeLabel(b))),
    [inspectionsData],
  );
  const [saveInspectionFormData] = useSaveInspectionFormDataMutation();
  const [updateInspectionForm] = useUpdateInspectionFormMutation();

  useEffect(() => {
    const load = async () => {
      const params = {
        is_deleted: false,
        is_threshold: true,
        limit: 1000000,
      };
      const results = await getInspectionTypes(params);
      setInspectionsData(results.data.results.filter(item => item.isActive));

      const juris = await getJurisdictions({
        ...GET_ALL_QUERY_PARAMS,
        ordering: 'name',
      });
      setJurisdictionData(juris.data.results);
    };
    load();
  }, []);

  useEffect(() => {
    if (initialValues) {
      const inspectionTypes = initialValues.inspectionTypes
        .map(item => inspectionsData.find(insp => insp.id === item))
        .filter(Boolean);
      if (inspectionTypes.length) {
         setValue('inspectionType', inspectionTypes);
      }

      if (initialValues.jurisdictions && initialValues.jurisdictions.length > 0 && jurisdictionData.length > 0) {
        setByJurisdiction(true);
      }
    }
  }, [initialValues, inspectionsData, jurisdictionData]);

  useEffect(() => {
    if (dataToSave) {
      const data: any = dataToSave;
      setDataToSave(undefined);
      const persist = async () => {
        const formData = new FormData();
        formData.append('name', data.name);
        formData.append('description', data.description);
        const files = getValues('file');
        if (files) {
          formData.append('files', files);
        }

        if (inspectionType) {
          inspectionType.forEach((i: any) => {
            formData.append('inspection_types', i.id);
          });
        } else {
          jurisdiction.forEach((i: any) => {
            formData.append('jurisdictions', i.id);
          });
        }

        // Editing ALWAYS patches the form in hand. This used to branch on
        // whether a file was attached and POST when one was, which silently
        // created a duplicate form and left the original untouched — the reason
        // a form could never be updated to a newer revision of its document.
        if (initialValues) {
          await updateInspectionForm({ data: formData, id: initialValues.id });
        } else {
          await saveInspectionFormData(formData);
        }
      };
      persist();
      setShowAdd(false);
    }
  }, [dataToSave]);

  useEffect(() => {
    if (byJurisdiction && initialValues !== undefined) {
      const id = Number(initialValues.jurisdictions[0]);
      const juri = jurisdictionData.filter(j => initialValues.jurisdictions.indexOf(j.id) > -1);
      setValue('jurisdiction', juri);
    }
  }, [byJurisdiction]);
  // Pages the saved markup actually uses. A replacement document with fewer
  // pages than this strands every field placed on the pages that disappear —
  // the markup keeps them, but nothing renders them.
  const editing: any = initialValues;
  const markupPageCount = Object.keys(editing?.formMarkup || {}).filter(
    key => typeof editing.formMarkup[key] === 'object' && editing.formMarkup[key] !== null,
  ).length;

  const checkPageCount = async (file: File) => {
    setPageWarning('');
    if (!initialValues || !markupPageCount || file.type !== 'application/pdf') return;
    try {
      const pages = (await PDFDocument.load(await file.arrayBuffer())).getPageCount();
      if (pages < markupPageCount) {
        setPageWarning(
          `Warning: this PDF has ${pages} page(s) but the form has fields on ${markupPageCount}. ` +
            `Fields on page ${pages + 1} and later will no longer appear. Placements are not deleted — ` +
            `re-upload a document with at least ${markupPageCount} pages to restore them.`,
        );
      }
    } catch {
      // An unreadable PDF is the server's problem to report; never block the
      // upload on the client's ability to parse it.
    }
  };

  const handleFileChange = e => {
    if (e.target.files[0]) {
      getBase64(e.target.files[0], result => setLocalUrl(result));
    }
    setFileName(e.target.files[0].name);
    checkPageCount(e.target.files[0]);
    setValue('file', e.target.files[0]);
  };

  const isAddDisabled = () => {
    if (initialValues) {
      return false;
    }
    const dropdownHasValue = !!inspectionType || !!jurisdiction;

    if (fileName === '' || !dropdownHasValue || !description || !name) {
      return true;
    }
    return false;
  };

  const onSubmit = async data => {
    setDataToSave(data);
  };

  const onCheckboxChanged = e => {
    // this handler is only available when createing a new record.
    if (initialValues) {
      return;
    }
    const { checked } = e.target;
    if (!checked) {
      setValue('jurisdiction', undefined);
      setValue('inspectionType', undefined);
    }
    setByJurisdiction(checked);
  };
  return (
    <>
      <Container disabled={isLoading}>
        <FormContainer autoComplete="off" aria-autocomplete="none" onSubmit={handleSubmit(onSubmit, err => console.log(err))}>
          <InputBaseContainer>
            <InputTextWithLabel
              name="name"
              error={errors.name}
              inputRef={register as any}
              label={labels.name}
              isRequired={isYupFieldRequired('name', fillableFormschema)}
            />
          </InputBaseContainer>
          <InputBaseContainer>
            <TextAreaWithLabel
              name="description"
              error={errors.description}
              label={labels.description}
              inputRef={register as any}
              isRequired={isYupFieldRequired('description', fillableFormschema)}
            />
          </InputBaseContainer>
          <ByJurisdictin>
            <input type="checkbox" checked={byJurisdiction} onChange={onCheckboxChanged} disabled={initialValues !== undefined} />
            by Jurisdiction
          </ByJurisdictin>

          {byJurisdiction && (
            <InputBaseContainer>
              <Controller
                name="jurisdiction"
                // defaultValue=""
                control={control}
                render={props => (
                  <InputSelectWithLabel
                    isMulti
                    selectAllActive
                    placeholder=""
                    menuPlacement="top"
                    isLoading={isLoading}
                    label={labels.jurisdictions}
                    // error={errors.jurisdiction}
                    getOptionValue={(item: any) => item.id || Math.random()}
                    getOptionLabel={(item: any) => item.name}
                    // isRequired={isYupFieldRequired('jurisdiction', schema)}
                    options={jurisdictionOptions}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...props}
                  />
                )}
              />
            </InputBaseContainer>
          )}
          {!byJurisdiction && (
            <InputBaseContainer>
              <Controller
                name="inspectionType"
                defaultValue=""
                control={control}
                render={props => (
                  <InputSelectWithLabel
                    isMulti
                    menuPlacement="top"
                    selectAllActive
                    placeholder=""
                    isLoading={isLoading}
                    isDisabled={isLoading}
                    label="Inspection Type"
                    error={errors.inspectionType}
                    getOptionValue={(item: any) => item.id}
                    getOptionLabel={inspectionTypeLabel}
                    labelChipsWithOptionLabel
                    // isRequired={isYupFieldRequired('jurisdictions', fillableFormschema)}
                    options={inspectionOptions}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...props}
                  />
                )}
              />
            </InputBaseContainer>
          )}
          {/* Shown when editing too: a form must be able to take a newer
              revision of its document without losing its placements. */}
          <InputBaseContainer>
            <Controller
              name="file"
              control={control}
              defaultValue={null}
              render={() => (
                <>
                  <StyledButton htmlFor="file-upload">
                    {initialValues ? 'Choose Form...' : 'Choose File...'}
                  </StyledButton>
                  <StyledInputFile
                    id="file-upload"
                    error={errors.file}
                    onChange={handleFileChange}
                    accept="application/pdf"
                    isRequired={!initialValues ? isYupFieldRequired('file', fillableFormschema) : false}
                  />
                  <StyledFileName>
                    <span>File</span>: {fileName || (editing?.visibleFilename ?? '')}
                  </StyledFileName>
                  {initialValues && (
                  <StyledFileHint>
                    Optional. Upload a new PDF to replace this form&apos;s document — all
                    placed fields are kept.
                  </StyledFileHint>
                    )}
                  {pageWarning && <StyledFileWarning>{pageWarning}</StyledFileWarning>}
                </>
                )}
            />
          </InputBaseContainer>
          <ButtonSection>
            {initialValues && IsUpdatePlaceholder && (
              <span
                style={{
                  color: 'red',
                  fontSize: '14px',
                  marginTop: '24px',
                }}
              >
                Please, set up placeholders
              </span>
            )}
            <ButtonWrapper>
              <ButtonWithLoader disabled={isAddDisabled()} type="submit">
                {initialValues ? 'Update' : 'Add'}
              </ButtonWithLoader>
            </ButtonWrapper>
          </ButtonSection>
        </FormContainer>
      </Container>
    </>
  );
};

export default AddNewForm;

interface Props {
  addButtonText?: string;
  setSelectedFontSize?: (data: any) => void;
  initialErrors?: FormServerError<FormFillableSetupType>;
  modalError?: boolean;
  initialValues?: FormFillableSetupType;
  selectedFontSize: number;
}

const Container = styled.fieldset`
  display: block;
  margin: 0;
  padding: 0;
  width: 100%;
  border: none;
  text-align: center;
`;

const FormContainer = styled.form`
  width: 100%;

  .MuiButton-root {
    margin-top: 24px;
  }
`;

const StyledInputFile = styled(InputFileContainer)`
  display: none;
`;
const StyledButton = styled.label`
  border: 1px solid #ccc;
  display: inline-block;
  padding: 6px 12px;
  cursor: pointer;
`;

const ButtonSection = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const StyledFileName = styled.h5`
  margin: 4px;
  & span {
    color: var(--primary);
  }
`;

const StyledFileHint = styled.p`
  margin: 2px 4px 0;
  font-size: 0.75rem;
  color: var(--neutral-500);
`;

const StyledFileWarning = styled.p`
  margin: 6px 4px 0;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid var(--danger-500);
  background: var(--danger-50);
  color: var(--danger-500);
  font-size: 0.75rem;
  line-height: 1.4;
`;

const ButtonWrapper = styled.div`
  margin-left: 10px;
`;

const StyledModalDraggable = styled(ModalDefaultDraggable)`
  height: 100%;
  overflow: inherit;
`;

const ByJurisdictin = styled.div`
  width: 100%;
  text-align: left;
  display: flex;
  gap: 10px;
  margin-top: 10px;
  margin-bottom: 10px;
`;
